import React, { useState, useEffect, createContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Home from "./pages/Home";
import Reservations from "./pages/ReservationsPage";
import Property from "./pages/PropertyPage";
import Reviews from "./pages/ReviewsPage";
import Promotions from "./pages/PromotionsPage";
import Analytics from "./pages/AnalyticsPage";
import Settings from "./components/Settings";
import AddPropertyListingPage from "./pages/listing/AddPropertyListingPage";
import AddImage from "./components/AddImage";
import Login from "./pages/Login";
import RoomRatesCalendar from "./components/RoomRatesCalendar";
import SetInventory from "./components/SetInventory";
import ToastTest from "./components/ToastTest";
import MyProperties from "./components/MyProperties";
import "react-toastify/dist/ReactToastify.css";
import useCustomToast from './hooks/useCustomToast';
import ToastProvider from './components/ToastProvider';
import { Button } from "@mui/material";
import PartnerRegistration from './components/PartnerRegistration';
import AdminPartnerList from './pages/AdminPartnerList';

import { Box } from "@mui/material";
import PropertyImages from "./pages/PropertyImages";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import useHotelList from './hooks/useHotelList';
import { resetHotelListCache } from './hooks/useHotelList';
import RoomTypes from "./pages/RoomTypes";
import Invoice from "./pages/Invoice";
import Support from "./pages/Support";
import SettlementsPage from "./pages/SettlementsPage";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutUs from "./pages/AboutUs";
import DownloadApp from "./pages/DownloadApp";
import GetAppRedirect from "./pages/GetAppRedirect";
import BlogIndex from "./pages/blog/BlogIndex";
import ZeroPercentCommissionOffer from "./pages/blog/ZeroPercentCommissionOffer";
import PropertyBoostPage from "./pages/PropertyBoostPage";
import CampaignPage from "./pages/CampaignPage";
import Performance from "./pages/Performance";
import PayInvoice from "./pages/PayInvoice";
import PaymentLinkGenerator from "./components/PaymentLinkGenerator";
import ListingPage from "./pages/listing/ListingPage";
import ListingByAdminPage from "./pages/listing/ListingByAdminPage";
import { clearListingDraft } from "./pages/listing/onboardingDraft";
import Seo from "./components/Seo";
import ExtranetPromoBanner from "./components/ExtranetPromoBanner";
import AppDownloadNudge from "./components/AppDownloadNudge";

// Layout component for reusing the main layout
const MainLayout = ({ children, isSidebarOpen, toggleSidebar, isMobile, onLogout, isAuthenticated }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: '#f9f9f9',
    }}>
      {/* Fixed Header */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
      }}>
        <Header
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          onLogout={onLogout}
          isAuthenticated={isAuthenticated}
        />
      </Box>

      {/* Sidebar */}
      <Box sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 1100,
        width: 'auto',
        transition: 'width 0.3s ease',
      }}>
        <Box sx={{ marginTop: '60px' }}>
          <Sidebar 
            isOpen={isSidebarOpen || !isMobile} 
            isMobile={isMobile}
            isAuthenticated={isAuthenticated}
            onLogout={onLogout}
          />
        </Box>
      </Box>

      {/* Dim overlay for mobile */}
      {isMobile && isSidebarOpen && (
        <Box
          onClick={toggleSidebar}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1050,
          }}
        />
      )}

      {/* Main Content */}
      <Box sx={{
        flexGrow: 1,
        marginTop: '60px',
        marginLeft: isMobile ? 0 : '80px',
        transition: 'margin-left 0.3s ease',
        padding: { xs: '16px', sm: '24px' },
        minHeight: 'calc(100vh - 60px)',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}>
        {children}
      </Box>

      <ExtranetPromoBanner />
    </Box>
  );
};

// Create AuthContext
export const AuthContext = createContext();

// Protected Route Component with better error handling and state persistence
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getDecryptedToken();
  const { errorToast } = useCustomToast();
  
  useEffect(() => {
    if (!token) {
      // Save the attempted location to redirect back after login
      sessionStorage.setItem('redirectPath', location.pathname + location.search);
      navigate("/login", { replace: true });
    }
  }, [token, navigate, errorToast, location]);
  
  if (!token) {
    return null;
  }
  
  return children;
};

// Simplified Protected Admin Route Component with improved navigation
const ProtectedAdminRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const secretKey = new URLSearchParams(location.search).get('key');
  const validKey = 'tabtrips2024';

  useEffect(() => {
    if (secretKey !== validKey) {
      navigate('/login', { replace: true });
    }
  }, [secretKey, navigate]);

  return secretKey === validKey ? children : null;
};

// 404 Page Component
const NotFound = () => {
  const navigate = useNavigate();
  
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f5f5f5'
    }}>
      <Box
        component="img"
        src="/404.png"
        alt="404"
        sx={{
          width: '200px',
          height: 'auto',
          marginBottom: '20px'
        }}
      />
      <h1>Page Not Found</h1>
      <p>Sorry, the page you are looking for does not exist.</p>
      <Button
        variant="contained"
        onClick={() => navigate('/', { replace: true })}
        sx={{
          marginTop: '20px',
          backgroundColor: '#FF7700',
          color: 'white',
          '&:hover': {
            backgroundColor: '#ff8800'
          }
        }}
      >
        Go to Home
      </Button>
    </Box>
  );
};

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}
function removeToken() {
  Cookies.remove(TOKEN_COOKIE_KEY);
}

function clearUserSession() {
  removeToken();
  localStorage.removeItem('selectedHotelId');
  localStorage.removeItem('selectedHotelName');
  localStorage.removeItem('selectedHostId');
  localStorage.removeItem('hotelsList');
  sessionStorage.clear();
}

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { successToast, errorToast } = useCustomToast();
  const { hotels } = useHotelList();

  // Initialize authentication state on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const token = getDecryptedToken();
    setIsAuthenticated(!!token);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.addEventListener("storage", checkAuth);
    return () => {
      window.removeEventListener("storage", checkAuth);
    };
  }, []);

  // Always set default hotel after login/hotel list fetch
  useEffect(() => {
    if (hotels && hotels.length > 0) {
      const currentHotelId = localStorage.getItem("selectedHotelId");
      if (!currentHotelId) {
        const firstHotel = hotels[0];
        localStorage.setItem("selectedHotelId", firstHotel.hotel_id);
        localStorage.setItem("selectedHotelName", firstHotel.hotel_name);
        if (firstHotel.host_id) {
          localStorage.setItem("selectedHostId", firstHotel.host_id);
        }
        // Dispatch event so sidebar updates immediately
        window.dispatchEvent(new Event('storage'));
      }
    }
  }, [hotels]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  const handleLogout = () => {
    clearUserSession();
    resetHotelListCache();
    // Listing draft lives in localStorage now; an explicit logout should not leave it for the next user.
    clearListingDraft();
    setIsAuthenticated(false);
    successToast("Logged out successfully");
  };

  const handleInvalidToken = () => {
    clearUserSession();
    resetHotelListCache();
    setIsAuthenticated(false);
    errorToast("Session expired. Please login again.");
  };

  const authContextValue = {
    isAuthenticated,
    setIsAuthenticated,
    handleLogout,
    handleInvalidToken
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      <Router
        basename={process.env.PUBLIC_URL}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Seo />
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}>
          <ToastProvider />
          <AppDownloadNudge />

          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
              isAuthenticated ? (
                <Navigate to={sessionStorage.getItem('redirectPath') || '/'} replace />
              ) : (
                <Login />
              )
            } />
            
            <Route path="/partner_registrationform" element={
              <ProtectedAdminRoute>
                <Box sx={{ width: '100%', minHeight: '100vh' }}>
                  <AdminPartnerList />
                </Box>
              </ProtectedAdminRoute>
            } />

            {/* Public Information Pages */}
            <Route path="/download-app" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <DownloadApp />
              </Box>
            } />

            <Route path="/get-app" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <GetAppRedirect />
              </Box>
            } />

            <Route path="/about-us" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <AboutUs />
              </Box>
            } />

            <Route path="/blog" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <BlogIndex />
              </Box>
            } />

            <Route path="/blog/zero-percent-commission-offer" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <ZeroPercentCommissionOffer />
              </Box>
            } />

            <Route path="/support" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <Support />
              </Box>
            } />

            <Route path="/terms-conditions" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <TermsConditions />
              </Box>
            } />

            <Route path="/privacy-policy" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <PrivacyPolicy />
              </Box>
            } />

            {/* Public Pay Invoice Route - Outside Protected Routes */}
            <Route path="/pay-invoice" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <PayInvoice />
              </Box>
            } />

            {/* Payment Link Generator - Admin Tool */}
            <Route path="/generate-payment-links" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <PaymentLinkGenerator />
              </Box>
            } />

            {/* Protected Routes */}
            <Route path="/" element={
              isAuthenticated ? (
                <ProtectedRoute>
                  <MainLayout 
                    isSidebarOpen={isSidebarOpen} 
                    toggleSidebar={toggleSidebar} 
                    isMobile={isMobile} 
                    onLogout={handleLogout}
                    isAuthenticated={isAuthenticated}
                  >
                    <MyProperties />
                  </MainLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" replace />
              )
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Home />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/reservations/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Reservations />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/settlements" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <SettlementsPage />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/property/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Property />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/reviews/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Reviews />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/promotions/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Promotions />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/property-boost/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <PropertyBoostPage />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/campaign/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <CampaignPage />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/performance" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Performance />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/analytics/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Analytics />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/settings/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Settings />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/add-property/*" element={
              <ProtectedRoute>
                <Box sx={{ width: '100%', minHeight: '100vh' }}>
                  <AddPropertyListingPage />
                </Box>
              </ProtectedRoute>
            } />

            <Route path="/add-image/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <AddImage />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/rates-inventory/:id/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <SetInventory />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/room-rates-calendar/:hotelId/*" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <RoomRatesCalendar />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/property-images" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <PropertyImages />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/hotel-images" element={<Navigate to="/property-images" replace />} />

            <Route path="/room-types" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <RoomTypes />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/invoice" element={
              <ProtectedRoute>
                <MainLayout 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar} 
                  isMobile={isMobile} 
                  onLogout={handleLogout}
                  isAuthenticated={isAuthenticated}
                >
                  <Invoice />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/listing" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <ListingPage />
              </Box>
            } />

            <Route path="/listingbyadmin" element={
              <Box sx={{ width: '100%', minHeight: '100vh' }}>
                <ListingByAdminPage />
              </Box>
            } />


            
            {/* 404 / fallback route */}
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <NotFound />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </Box>
      </Router>
    </AuthContext.Provider>
  );
};

export default App;